import React, { useCallback, useEffect, useRef, useState } from 'react';
import { deleteCommunication, fetchRecordCommunications, patchCommunication, postRecordCommunication, restoreCommunication } from '../api';
import { CommunicationChannel, CommunicationType, RecordCommunication } from '../types';
import { formatDate } from '../utils/formatters';

interface CommunicationHistorySectionProps {
  recordUuid: string;
  refreshTrigger?: number;
}

const TYPE_LABELS: Record<CommunicationType, string> = {
  SERVICE_INTAKE: 'Przyjęto sprzęt',
  RECEIVED_CONFIRMATION: 'Potwierdzenie przyjęcia',
  REPAIR_UPDATE: 'Aktualizacja naprawy',
  READY_FOR_PICKUP: 'Gotowe do odbioru',
  CUSTOM: 'Wiadomość własna',
  MANUAL_NOTE: 'Notatka',
  STATUS_CHANGE: 'Zmiana statusu',
};

export const CommunicationHistorySection: React.FC<CommunicationHistorySectionProps> = ({
  recordUuid,
  refreshTrigger = 0,
}) => {
  const [comms, setComms] = useState<RecordCommunication[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedUuids, setExpandedUuids] = useState<Record<string, boolean>>({});

  // Manual note entry modal/form state
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteChannel, setNoteChannel] = useState<CommunicationChannel>('INTERNAL_NOTE');
  const [noteContent, setNoteContent] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);

  // Deletion confirmation & Undo state
  const [confirmDeleteUuid, setConfirmDeleteUuid] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [undoToast, setUndoToast] = useState<{ uuid: string; message: string } | null>(null);
  const undoTimeoutRef = useRef<any>(null);

  // Manual note edit state
  const [editingUuid, setEditingUuid] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchRecordCommunications(recordUuid);
      // Sort newest on top for Web timeline
      setComms([...data].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } catch (e) {
      // Ignore background fetch errors
    } finally {
      setIsLoading(false);
    }
  }, [recordUuid]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory, refreshTrigger]);

  const toggleExpand = (uuid: string) => {
    setExpandedUuids((prev) => ({
      ...prev,
      [uuid]: !prev[uuid],
    }));
  };

  const handleSaveManualNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteContent.trim()) {
      setNoteError('Treść wpisu nie może być pusta');
      return;
    }

    setIsSavingNote(true);
    setNoteError(null);
    try {
      await postRecordCommunication(recordUuid, {
        type: 'MANUAL_NOTE',
        channel: noteChannel,
        action: 'MANUAL_ENTRY',
        source: 'WEB',
        content: noteContent.trim(),
        idempotencyKey: crypto.randomUUID(),
      });
      setNoteContent('');
      setIsAddingNote(false);
      await loadHistory();
    } catch (err: any) {
      setNoteError(err.message || 'Nie udało się zapisać wpisu');
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleCancelManualNote = () => {
    setIsAddingNote(false);
    setNoteContent('');
    setNoteError(null);
  };

  // Delete Action
  const handleDeleteClick = (uuid: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDeleteUuid(uuid);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteUuid) return;
    const targetUuid = confirmDeleteUuid;
    setIsDeleting(true);
    try {
      await deleteCommunication(targetUuid);
      setConfirmDeleteUuid(null);
      await loadHistory();

      // Show Undo Snackbar
      if (undoTimeoutRef.current) clearTimeout(undoTimeoutRef.current);
      setUndoToast({ uuid: targetUuid, message: 'Wpis został usunięty z historii' });
      undoTimeoutRef.current = setTimeout(() => {
        setUndoToast(null);
      }, 7000);
    } catch (err: any) {
      alert(err.message || 'Nie udało się usunąć wpisu');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDeleteUuid(null);
  };

  // Undo Action
  const handleUndoDelete = async (uuid: string) => {
    if (undoTimeoutRef.current) clearTimeout(undoTimeoutRef.current);
    setUndoToast(null);
    try {
      await restoreCommunication(uuid);
      await loadHistory();
    } catch (err: any) {
      alert(err.message || 'Nie udało się przywrócić wpisu');
    }
  };

  // Edit Action
  const handleStartEdit = (item: RecordCommunication, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingUuid(item.uuid);
    setEditingContent(item.content);
    setEditError(null);
    setExpandedUuids((prev) => ({ ...prev, [item.uuid]: true }));
  };

  const handleSaveEdit = async (uuid: string, e: React.FormEvent) => {
    e.preventDefault();
    if (!editingContent.trim()) {
      setEditError('Treść wpisu nie może być pusta');
      return;
    }

    setIsSavingEdit(true);
    setEditError(null);
    try {
      await patchCommunication(uuid, editingContent.trim());
      setEditingUuid(null);
      await loadHistory();
    } catch (err: any) {
      setEditError(err.message || 'Nie udało się zaktualizować notatki');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingUuid(null);
    setEditingContent('');
    setEditError(null);
  };

  return (
    <div className="card-container" style={{ marginTop: '16px' }}>
      <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
        <span>📜 Historia kontaktu i zdarzeń</span>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {!isAddingNote && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setIsAddingNote(true)}
              style={{ fontSize: '0.8rem', padding: '3px 10px' }}
            >
              + Dodaj wpis
            </button>
          )}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={loadHistory}
            disabled={isLoading}
            style={{ fontSize: '0.8rem', padding: '3px 8px' }}
          >
            {isLoading ? 'Odświeżanie...' : '🔄 Odśwież'}
          </button>
        </div>
      </div>

      {/* Undo Toast Bar */}
      {undoToast && (
        <div
          style={{
            margin: '10px 0',
            padding: '10px 14px',
            backgroundColor: '#1e293b',
            color: '#f8fafc',
            borderRadius: '6px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.875rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <span>{undoToast.message}</span>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => handleUndoDelete(undoToast.uuid)}
            style={{
              backgroundColor: '#2563eb',
              color: '#ffffff',
              padding: '3px 12px',
              fontSize: '0.825rem',
              fontWeight: 600,
              borderRadius: '4px',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            ↩ Cofnij
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal / Inline Alert */}
      {confirmDeleteUuid && (
        <div
          style={{
            margin: '10px 0',
            padding: '12px 14px',
            backgroundColor: '#fef2f2',
            border: '1px solid #f87171',
            borderRadius: '6px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '8px',
          }}
        >
          <span style={{ color: '#991b1b', fontWeight: 600, fontSize: '0.875rem' }}>
            Czy na pewno usunąć ten wpis z historii?
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleCancelDelete}
              disabled={isDeleting}
              style={{ fontSize: '0.8rem', padding: '3px 10px' }}
            >
              Anuluj
            </button>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              style={{ fontSize: '0.8rem', padding: '3px 12px', backgroundColor: '#dc2626', color: '#fff' }}
            >
              {isDeleting ? 'Usuwanie...' : 'Usuń wpis'}
            </button>
          </div>
        </div>
      )}

      {/* Manual Entry Form */}
      {isAddingNote && (
        <form
          onSubmit={handleSaveManualNote}
          style={{
            margin: '12px 0 16px 0',
            padding: '14px',
            borderRadius: '6px',
            border: '1px solid var(--primary, #2563eb)',
            backgroundColor: 'var(--bg-muted, #f8fafc)',
          }}
        >
          <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '10px', color: 'var(--text-color)' }}>
            Nowy wpis w historii zdarzeń
          </div>

          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label htmlFor="manual-entry-type" style={{ display: 'block', fontSize: '0.825rem', fontWeight: 600, marginBottom: '4px' }}>
              Typ wpisu:
            </label>
            <select
              id="manual-entry-type"
              className="form-control"
              value={noteChannel}
              onChange={(e) => setNoteChannel(e.target.value as CommunicationChannel)}
              style={{ width: '100%', maxWidth: '300px', padding: '6px 8px', fontSize: '0.875rem' }}
            >
              <option value="INTERNAL_NOTE">Notatka wewnętrzna</option>
              <option value="PHONE_CALL">Rozmowa telefoniczna</option>
              <option value="CUSTOMER_AGREEMENT">Ustalenie z klientem</option>
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label htmlFor="manual-entry-content" style={{ display: 'block', fontSize: '0.825rem', fontWeight: 600, marginBottom: '4px' }}>
              Treść wpisu:
            </label>
            <textarea
              id="manual-entry-content"
              className="form-control"
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="np. Klient zaakceptował wymianę dolnej płytki. Część zamówiona."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '8px',
                fontSize: '0.875rem',
                lineHeight: 1.4,
                resize: 'vertical',
              }}
            />
          </div>

          {noteError && (
            <div style={{ color: '#dc2626', fontSize: '0.825rem', marginBottom: '10px' }}>
              {noteError}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleCancelManualNote}
              disabled={isSavingNote}
            >
              Anuluj
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={isSavingNote || !noteContent.trim()}
            >
              {isSavingNote ? 'Zapisywanie...' : 'Zapisz w historii'}
            </button>
          </div>
        </form>
      )}

      {isLoading && comms.length === 0 ? (
        <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          Ładowanie historii zdarzeń...
        </div>
      ) : comms.length === 0 ? (
        <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '0.9rem', fontStyle: 'italic' }}>
          Brak zarejestrowanych zdarzeń i kontaktu dla tego zgłoszenia.
        </div>
      ) : (
        <div className="comm-timeline" style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
          {comms.map((item) => {
            const isExpanded = expandedUuids[item.uuid] ?? false;
            const isEditingThis = editingUuid === item.uuid;

            // Permissions
            const isSystemEvent = item.type === 'SERVICE_INTAKE' || item.type === 'STATUS_CHANGE' || item.action === 'SYSTEM_EVENT';
            const canDelete = !isSystemEvent;
            const canEdit = item.type === 'MANUAL_NOTE' && item.action === 'MANUAL_ENTRY';

            // Human-readable action badge text and colors
            let actionBadgeText = item.action as string;
            let actionBgColor = '#f3f4f6';
            let actionTextColor = '#374151';

            if (item.action === 'COPIED_FOR_SENDING') {
              actionBadgeText = 'Skopiowano do wysłania';
              actionBgColor = '#e0f2fe';
              actionTextColor = '#0369a1';
            } else if (item.action === 'HANDED_TO_SMS_APP') {
              actionBadgeText = 'Przekazano do aplikacji SMS';
              actionBgColor = '#d1fae5';
              actionTextColor = '#047857';
            } else if (item.action === 'SYSTEM_EVENT') {
              actionBadgeText = 'Zdarzenie systemowe';
              actionBgColor = '#f3e8ff';
              actionTextColor = '#6b21a8';
            } else if (item.action === 'MANUAL_ENTRY') {
              actionBadgeText = 'Wpis ręczny';
              actionBgColor = '#fef3c7';
              actionTextColor = '#b45309';
            }

            const sourceText = item.source === 'WEB' ? 'Web CRM' : item.source === 'ANDROID' ? 'Android' : item.source;

            // Type title with channel distinction
            let typeLabel = TYPE_LABELS[item.type] || item.type;
            if (item.type === 'MANUAL_NOTE') {
              if (item.channel === 'PHONE_CALL') typeLabel = 'Rozmowa telefoniczna';
              else if (item.channel === 'CUSTOMER_AGREEMENT') typeLabel = 'Ustalenie z klientem';
              else if (item.channel === 'INTERNAL_NOTE') typeLabel = 'Notatka wewnętrzna';
            }

            const isEdited = Boolean(item.updatedAt || item.revision > 1);

            return (
              <div
                key={item.uuid}
                style={{
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  padding: '10px 14px',
                  backgroundColor: 'var(--bg-card)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    flexWrap: 'wrap',
                    gap: '6px',
                  }}
                  onClick={() => toggleExpand(item.uuid)}
                >
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{typeLabel}</span>
                    <span
                      style={{
                        fontSize: '0.75rem',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        backgroundColor: actionBgColor,
                        color: actionTextColor,
                        fontWeight: 600,
                      }}
                    >
                      {actionBadgeText}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      • {sourceText}
                    </span>
                    {isEdited && (
                      <span style={{ fontSize: '0.75rem', color: '#64748b', fontStyle: 'italic' }}>
                        • Edytowano
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {formatDate(item.createdAt)}
                    </span>

                    {/* Action buttons */}
                    {canEdit && !isEditingThis && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={(e) => handleStartEdit(item, e)}
                        title="Edytuj treść notatki"
                        style={{ fontSize: '0.75rem', padding: '2px 6px', lineHeight: 1 }}
                      >
                        ✏️ Edytuj
                      </button>
                    )}

                    {canDelete && (
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={(e) => handleDeleteClick(item.uuid, e)}
                        title="Usuń ten wpis z historii"
                        style={{ fontSize: '0.75rem', padding: '2px 6px', color: '#dc2626', borderColor: '#fca5a5', lineHeight: 1 }}
                      >
                        🗑️ Usuń
                      </button>
                    )}

                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: '4px' }}>
                      {isExpanded ? '▲' : '▼'}
                    </span>
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ marginTop: '10px' }}>
                    {isEditingThis ? (
                      <form onSubmit={(e) => handleSaveEdit(item.uuid, e)} onClick={(e) => e.stopPropagation()}>
                        <textarea
                          className="form-control"
                          value={editingContent}
                          onChange={(e) => setEditingContent(e.target.value)}
                          style={{
                            width: '100%',
                            minHeight: '90px',
                            padding: '8px',
                            fontSize: '0.875rem',
                            lineHeight: 1.4,
                            resize: 'vertical',
                            marginBottom: '8px',
                          }}
                        />
                        {editError && (
                          <div style={{ color: '#dc2626', fontSize: '0.825rem', marginBottom: '8px' }}>
                            {editError}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={handleCancelEdit}
                            disabled={isSavingEdit}
                          >
                            Anuluj
                          </button>
                          <button
                            type="submit"
                            className="btn btn-primary btn-sm"
                            disabled={isSavingEdit || !editingContent.trim()}
                          >
                            {isSavingEdit ? 'Zapisywanie...' : 'Zapisz zmiany'}
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div
                        style={{
                          padding: '10px',
                          borderRadius: '4px',
                          backgroundColor: 'var(--bg-muted, #f8fafc)',
                          borderLeft: '3px solid var(--primary, #2563eb)',
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'sans-serif',
                          fontSize: '0.875rem',
                          lineHeight: '1.4',
                          color: 'var(--text-color)',
                        }}
                      >
                        {item.content ? item.content.replace(/\\n/g, '\n') : ''}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


